/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Tabbed } from '../../components/LoanInfoTabs';
import EnvelopeWithBox from '../EnvelopeWithBox';
import Styles from '../../css/LoanInformation.module.css';
import { Fragment, useCallback, useState } from 'react';

import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import {
  convertDateInputTo8AMLocal,
  dateToEpoch,
  formatDateForInputWithOffset,
  formatSSN,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
  safeFullName,
} from '../../common/UtilityFunctions';
import { useViewingContext } from '../../contexts/ViewingContext';
import { Edit } from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useQidList from '../../common/hooks/useQidList';

type BorrowerInfoProps = {
  showEntityInfo: boolean;
  setShowEntityInfo: (show: boolean) => void;
};

const BorrowerInfo = ({ showEntityInfo, setShowEntityInfo }: BorrowerInfoProps) => {
  const {
    loanData,
    handleLoanChange,
    borrowersArr,
    handleLoanBorrowersChange,
    addedBorrower,
    handleAddBorrowerChange,
    privacyModalNumber,
    setPrivacyModalNumber,
    setNameModalBorrowerNumber,
    handleAnytimeChange,
    handlePropChange,
    propData,
  } = useLoanInformationContext();
  const { isServicerView } = useViewingContext();
  const { enqueueSnackbar } = useSnackbar();

  const currentPrimaryNumber = (loanData?.primaryBorrowerNumber ?? loanData?.primaryBorrowerNumber) || null;
  const { qid: emailStatusQID } = useQidList('BRW1_CD.409');

  const isBorrowerValidForRemoving = useCallback(
    (b: BorrowerData) => {
      if (isStrEmptyOrNull(b?.borrowerNumber)) return false;
      if (b.borrowerNumber === currentPrimaryNumber) {
        enqueueSnackbar('Make someone else Primary before removing the current Primary.', { variant: 'error' });

        return false;
      }

      const candidates = borrowersArr
        .filter((x): x is BorrowerData => !!x && !!x.borrowerOnNote)
        .filter((x) => x.borrowerNumber !== b.borrowerNumber);

      if (candidates.length === 0) {
        enqueueSnackbar(
          'Add another on-note borrower or make someone else Primary before removing the current Primary.',
          { variant: 'error' },
        );

        return false;
      }
      return true;
    },
    [currentPrimaryNumber, borrowersArr],
  );

  const [showNewBorrower, setShowNewBorrower] = useState(false);

  return (
    <EnvelopeWithBox blockName='Borrower Information'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              {borrowersArr
                .filter((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
                .map((borrower, idx) => (
                  <Fragment key={borrower.borrowerNumber ?? String(idx)}>
                    <tr>
                      <th className={Styles.separatorCellHeader}>
                        <h4>
                          Borrower {borrower.borrowerNumber}
                          {borrower.borrowerNumber === currentPrimaryNumber ? ' - Primary' : ''}:
                        </h4>
                      </th>
                      <td>
                        {safeFullName(borrower)}
                        {isServicerView && (
                          <>
                            <button
                              type='button'
                              className={Styles.modalLink}
                              onClick={(e) => {
                                e.preventDefault();
                                setNameModalBorrowerNumber(borrower.borrowerNumber);
                              }}
                              title='Edit name'
                              style={{ marginLeft: 8 }}
                            >
                              Edit name
                            </button>
                            <button
                              type='button'
                              className={Styles.modalLink}
                              onClick={(e) => {
                                e.preventDefault();
                                setPrivacyModalNumber(borrower.borrowerNumber);
                              }}
                              title='Edit privacy fields'
                            >
                              <Edit />
                            </button>
                            {borrower.borrowerNumber !== currentPrimaryNumber && (
                              <button
                                type='button'
                                className={Styles.deleteButton}
                                onClick={(e) => {
                                  handleAnytimeChange('cancel');
                                  handleLoanBorrowersChange('cancel', currentPrimaryNumber + '.bttc_array');
                                  handleLoanChange('edit', 'primaryBorrowerNumber', borrower.borrowerNumber);
                                }}
                                title='Make this borrower primary'
                                style={{ marginLeft: 8 }}
                              >
                                Make Primary
                              </button>
                            )}
                            <button
                              type='button'
                              className={Styles.deleteButton}
                              onClick={(e) => {
                                if (isBorrowerValidForRemoving(borrower)) {
                                  handleLoanBorrowersChange(
                                    'edit',
                                    borrower.borrowerNumber + '.borrowerTypeKeyword',
                                    'REMOVED',
                                  );
                                }
                              }}
                              title='Remove borrower'
                              style={{ marginLeft: 8 }}
                            >
                              Remove
                            </button>
                            {borrower.borrowerNumber === currentPrimaryNumber &&
                              propData?.vestHeldInEntity === true && (
                                <label className={`${Styles.checkboxGroup} ${Styles.entityCheckboxSpacing}`}>
                                  <input
                                    type='checkbox'
                                    className={Styles.checkboxSm}
                                    checked={showEntityInfo}
                                    onChange={(e) => {
                                      setShowEntityInfo(e.target.checked);
                                    }}
                                  />
                                  <span>Show Entity Information</span>
                                </label>
                              )}
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>SSN:</th>
                      <td>
                        <input
                          type='text'
                          name={`ssn-${borrower.borrowerNumber}`}
                          value={formatSSN(borrower.ssn)}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/\D/g, '');
                            if (rawValue.length <= 9) {
                              handleLoanBorrowersChange('edit', borrower.borrowerNumber + '.ssn', rawValue);
                            }
                          }}
                          placeholder='SSN'
                          maxLength={11}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Date of Birth {idx === 0 ? '1:' : `${idx + 1}:`}</th>
                      <td>
                        <input
                          type='date'
                          className={Styles.calicon}
                          name={`DOB-${borrower.borrowerNumber}`}
                          value={borrower.DOB ? formatDateForInputWithOffset(borrower.DOB) : ''}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (!value) {
                              e.target.value = null;
                              handleLoanBorrowersChange('edit', borrower.borrowerNumber + '.DOB', '');
                              return;
                            }

                            const localDate = convertDateInputTo8AMLocal(value);
                            const ts = dateToEpoch(localDate);

                            handleLoanBorrowersChange('edit', borrower.borrowerNumber + '.DOB', ts);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Email {idx === 0 ? '1:' : `${idx + 1}:`}</th>
                      <td>
                        <input
                          type='email'
                          name={`email-${borrower.borrowerNumber}`}
                          value={borrower.emailAddress}
                          onChange={(e) => {
                            handleLoanBorrowersChange(
                              'edit',
                              borrower.borrowerNumber + '.emailAddress',
                              e.target.value,
                            );
                          }}
                          placeholder='example@example.com'
                          className={Styles.firstInputRow}
                        />
                        <select
                          name={`emailAddressStatus-${borrower.borrowerNumber}`}
                          value={borrower?.emailAddressStatus || ''}
                          onChange={(e) => {
                            handleLoanBorrowersChange(
                              'edit',
                              borrower.borrowerNumber + '.emailAddressStatus',
                              e.target.value,
                            );
                          }}
                          defaultValue=''
                          className={Styles.emailDncRow}
                        >
                          <option value=''>Select Email Status</option>
                          {emailStatusQID.map((opt, j) => (
                            <option key={j} value={opt.code}>
                              {opt.description}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <NavLink to='/go-green'>Go Green</NavLink>
                      </th>
                      <td>
                        <select
                          name={`goGreen-${borrower.borrowerNumber}`}
                          value={borrower.goGreen ? 'true' : 'false'}
                          disabled={true}
                        >
                          <option value='true'>Yes</option>
                          <option value='false'>No</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th>E-statement:</th>
                      <td>
                        <select
                          name={`EStatement-${borrower.borrowerNumber}`}
                          value={borrower.EStatement ? 'true' : 'false'}
                          onChange={(e) =>
                            handleLoanBorrowersChange(
                              'edit',
                              borrower.borrowerNumber + '.EStatement',
                              e.target.value === 'true',
                            )
                          }
                        >
                          <option value='true'>Yes</option>
                          <option value='false'>No</option>
                        </select>
                      </td>
                    </tr>
                  </Fragment>
                ))}
              {borrowersArr
                .filter((b) => !b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
                .map((nbp, i) => (
                  <tr key={nbp.borrowerNumber ?? `nbp_${i}`}>
                    <th className={Styles.separatorCellHeader}>
                      <h4>{`Non-Borrowing Party ${i + 1}:`}</h4>
                    </th>
                    <td>
                      {safeFullName(nbp)}
                      {isServicerView && (
                        <button
                          type='button'
                          className={Styles.deleteButton}
                          onClick={(e) => {
                            if (!nbp.borrowerNumber) return;
                            handleLoanBorrowersChange('edit', nbp.borrowerNumber + '.borrowerTypeKeyword', 'REMOVED');
                          }}
                          title='Remove non-borrowing party'
                          style={{ marginLeft: 8 }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
            {isServicerView && (
              <tfoot>
                {!showNewBorrower && isObjEmptyOrNull(addedBorrower) ? (
                  <tr>
                    <td colSpan={2}>
                      <button className={Styles.addBtn} onClick={() => setShowNewBorrower(true)}>
                        + Add Borrower / NBP
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <th>
                      New{' '}
                      {'borrowerType' in addedBorrower && addedBorrower.borrowerType == '5'
                        ? 'Non-Borrowing Party'
                        : 'Borrower'}
                      :
                    </th>
                    <td className={Styles.addRowControls}>
                      <input
                        type='text'
                        className={`${Styles.addInputText} ${Styles.addInputFirst}`}
                        value={addedBorrower?.firstName ? addedBorrower?.firstName : ''}
                        onChange={(e) => handleAddBorrowerChange('edit', 'firstName', e.target.value)}
                        placeholder='First name (required)'
                        aria-label='First name'
                      />
                      <input
                        type='text'
                        className={`${Styles.addInputText} ${Styles.addInputMiddle}`}
                        value={addedBorrower?.middleName ? addedBorrower?.middleName : ''}
                        onChange={(e) => handleAddBorrowerChange('edit', 'middleName', e.target.value)}
                        placeholder='Middle'
                        aria-label='Middle name'
                      />
                      <input
                        type='text'
                        className={`${Styles.addInputText} ${Styles.addInputGrow}`}
                        value={addedBorrower?.lastName ? addedBorrower?.lastName : ''}
                        onChange={(e) => handleAddBorrowerChange('edit', 'lastName', e.target.value)}
                        placeholder='Last name (required)'
                        aria-label='Last name'
                      />
                      <input
                        type='text'
                        className={`${Styles.addInputText} ${Styles.addInputGrow}`}
                        value={addedBorrower?.suffix ? addedBorrower?.suffix : ''}
                        onChange={(e) => handleAddBorrowerChange('edit', 'suffix', e.target.value)}
                        placeholder='Suffix'
                        aria-label='Suffix'
                      />
                      <label className={Styles.checkboxGroup}>
                        <input
                          type='checkbox'
                          className={Styles.checkboxSm}
                          checked={'borrowerType' in addedBorrower ? addedBorrower?.borrowerType == '1' : true}
                          onChange={(e) =>
                            handleAddBorrowerChange('edit', 'borrowerType', e.target.checked ? '1' : '5')
                          }
                        />
                        <span>Active borrower (on note)</span>
                      </label>

                      <button
                        className={Styles.cancelBtn}
                        onClick={() => {
                          handleAddBorrowerChange('cancel', '');
                          setShowNewBorrower(false);
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default BorrowerInfo;
